<template>
  <aside class="automation-runs">
    <header class="automation-runs__header">
      <div>
        <h3>{{ $t('automations_page.runs') }}</h3>
        <p v-if="selectedRun" class="automation-runs__selected">
          {{ $t('automations_page.run_selected') }}
        </p>
      </div>
      <Btn
        v-if="selectedRun"
        variant="plain"
        size="sm"
        type="button"
        @click="$emit('clear')"
      >
        {{ $t('automations_page.run_clear') }}
      </Btn>
    </header>

    <div v-if="loading" class="automation-runs__empty">Laden...</div>
    <div v-else-if="!runs.length" class="automation-runs__empty">
      {{ $t('automations_page.no_runs') }}
    </div>
    <ul v-else class="automation-runs__list">
      <li v-for="run in runs" :key="run.id">
        <button
          class="automation-runs__item"
          :class="{ 'automation-runs__item--active': selectedRun?.id === run.id }"
          type="button"
          @click="$emit('select', run)"
        >
          <Label :variant="statusVariant(run.status)" size="sm" show-dot>
            {{ $t(`automations_page.run_status.${run.status}`) }}
          </Label>
          <span class="automation-runs__time">{{ formatTime(run.started_at) }}</span>
          <span v-if="runSubject(run)" class="automation-runs__subject">
            {{ runSubject(run) }}
          </span>
          <span v-if="run.error_message" class="automation-runs__error">
            {{ run.error_message }}
          </span>
        </button>
      </li>
    </ul>

    <div v-if="selectedRun?.steps?.length" class="automation-runs__steps">
      <h4>{{ $t('automations_page.run_steps') }}</h4>
      <ul>
        <li v-for="step in selectedRun.steps" :key="step.id" class="automation-runs__step">
          <Label :variant="statusVariant(step.status)" size="sm">
            {{ $t(`automations_page.run_status.${step.status}`) }}
          </Label>
          <span v-if="step.error_message" class="automation-runs__error">
            {{ step.error_message }}
          </span>
        </li>
      </ul>
    </div>
  </aside>
</template>

<script>
  import { getCurrentInstance } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import Label from '@/components/partials/Label.vue';

  export default {
    name: 'AutomationRunsPanel',

    components: {
      Btn,
      Label,
    },

    emits: ['select', 'clear'],

    props: {
      runs: {
        type: Array,
        default: () => [],
      },
      selectedRun: {
        type: Object,
        default: null,
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },

    setup() {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const statusVariant = (status) => {
        if (status === 'completed') return 'success';
        if (status === 'failed') return 'danger';
        if (status === 'in_progress') return 'info';
        return 'default';
      };

      const formatTime = (value) => {
        if (!value) return '—';
        return new Date(value).toLocaleString('nl-NL', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        });
      };

      const runSubject = (run) => {
        if (run.deal?.title) {
          return `${$t('automations_page.run_deal')}: ${run.deal.title}`;
        }
        if (run.deal?.lead_name) {
          return `${$t('automations_page.run_deal')}: ${run.deal.lead_name}`;
        }
        if (run.proposal?.proposal_number || run.proposal?.title) {
          return `${$t('automations_page.run_proposal')}: ${
            run.proposal.proposal_number || run.proposal.title
          }`;
        }
        if (run.submission_id) {
          return `${$t('automations_page.run_submission')}`;
        }
        return '';
      };

      return {
        statusVariant,
        formatTime,
        runSubject,
      };
    },
  };
</script>

<style scoped lang="scss">
  .automation-runs {
    width: 360px;
    flex-shrink: 0;
    border-left: 1px solid var(--slate-200);
    background: #fff;
    display: flex;
    flex-direction: column;
    min-height: 0;

    &__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 0.75rem;
      padding: 1.25rem 1.25rem 1rem;
      border-bottom: 1px solid var(--slate-100);

      h3 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
      }
    }

    &__selected {
      margin: 0.25rem 0 0;
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__list {
      list-style: none;
      margin: 0;
      padding: 0.5rem;
      overflow-y: auto;
      flex: 1;
    }

    &__item {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
      padding: 0.75rem;
      border: 0;
      border-radius: 8px;
      background: transparent;
      text-align: left;
      cursor: pointer;

      @include hover {
        background: var(--slate-50);
      }

      &--active {
        background: var(--brand-50);
      }
    }

    &__time,
    &__subject {
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__error {
      font-size: 0.75rem;
      color: var(--red-600);
    }

    &__empty {
      padding: 1.5rem 1.25rem;
      font-size: 0.875rem;
      color: var(--slate-500);
    }

    &__steps {
      border-top: 1px solid var(--slate-100);
      padding: 1rem 1.25rem;
      max-height: 40%;
      overflow-y: auto;

      h4 {
        margin: 0 0 0.75rem;
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.02em;
        color: var(--slate-500);
      }

      ul {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
    }

    &__step {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
    }
  }
</style>
